import { DateTime } from "luxon";
import { useMemo } from "react";

export enum DateFilterPreset {
  /** Showing only captures taken the last day */
  lastCapture = "Last capture",
  /** Showing only captures taken this week */
  thisWeek = "This week",
  /** Showing only captures taken this month */
  thisMonth = "This month",
}

/** Which of the quick presets the active date range corresponds to, if any */
export type SelectedPresets = {
  isLastCapture: boolean;
  isThisWeek: boolean;
  isThisMonth: boolean;
};

const NO_PRESET_SELECTED: SelectedPresets = {
  isLastCapture: false,
  isThisWeek: false,
  isThisMonth: false,
};

/**
 * @returns The date range a quick preset stands for
 * @param preset The preset the user picked
 * @param lastCaptureDate The date of the newest capture in the project
 */
export function rangeForPreset(
  preset: DateFilterPreset,
  lastCaptureDate: Date | undefined,
): [start: Date, end: Date] {
  if (preset === DateFilterPreset.lastCapture && lastCaptureDate) {
    return [lastCaptureDate, lastCaptureDate];
  }
  return [
    DateTime.now().startOf(presetUnit(preset)).toJSDate(),
    DateTime.now().endOf("day").toJSDate(),
  ];
}

/**
 * The store keeps only a start/end pair, so which preset looks selected has to be derived
 * back from the range on every render.
 *
 * @param selectedStartDate The currently selected start date
 * @param selectedEndDate The currently selected end date
 * @param lastCaptureDate The date of the newest capture in the project
 * @returns whether any preset is selected
 */
export function useIsPresetSelected(
  selectedStartDate: Date | undefined,
  selectedEndDate: Date | undefined,
  lastCaptureDate: Date | undefined,
): SelectedPresets {
  return useMemo(() => {
    if (!selectedStartDate || !selectedEndDate) {
      return NO_PRESET_SELECTED;
    }

    const lastCaptureDateTime = lastCaptureDate
      ? DateTime.fromJSDate(lastCaptureDate)
      : DateTime.now().endOf("day");

    const endOfDay = DateTime.now().endOf("day");
    const startOfWeek = DateTime.now().startOf("week");
    const startOfMonth = DateTime.now().startOf("month");

    const start = DateTime.fromJSDate(selectedStartDate);
    const end = DateTime.fromJSDate(selectedEndDate);

    if (
      end.hasSame(lastCaptureDateTime, "day") &&
      start.hasSame(lastCaptureDateTime, "day")
    ) {
      return { ...NO_PRESET_SELECTED, isLastCapture: true };
    }

    if (end.hasSame(endOfDay, "day")) {
      if (start.hasSame(startOfWeek, "day")) {
        return { ...NO_PRESET_SELECTED, isThisWeek: true };
      }

      if (start.hasSame(startOfMonth, "day")) {
        return { ...NO_PRESET_SELECTED, isThisMonth: true };
      }
    }

    return NO_PRESET_SELECTED;
  }, [selectedStartDate, selectedEndDate, lastCaptureDate]);
}

/**
 * When setting the 'from' date, this function checks whether the 'to' date should be set to the last capture date.
 *
 * @param date The 'from' date being set
 * @param selectedEndDate The currently selected 'to' date
 * @param lastCaptureDate The date of the last capture in the project
 * @returns whether the 'to' date should be set to the last capture date.
 */
export function shouldSetLastCaptureDate(
  date: Date | undefined,
  selectedEndDate: Date | undefined,
  lastCaptureDate: Date | undefined,
): boolean {
  return (
    !!date && !selectedEndDate && !!lastCaptureDate && date < lastCaptureDate
  );
}

function presetUnit(preset: DateFilterPreset): "day" | "week" | "month" {
  switch (preset) {
    case DateFilterPreset.lastCapture:
      return "day";
    case DateFilterPreset.thisWeek:
      return "week";
    case DateFilterPreset.thisMonth:
      return "month";
  }
}
